import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import MergeOutlinedIcon from '@mui/icons-material/MergeOutlined'
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { PromptDialog } from '@/components'
import { Loader } from '@/components/Loader/Loader'
import { useAppContext, useAppStore } from '@/hooks'
import { BranchRecord } from '@/pages/User/pages/Branches/types'
import {
	useCreateMergeRequestMutation,
	useDecideMutation,
	useExecuteMergeMutation,
	useSaveResolutionMutation,
	useUpdateMergeRequestStatusMutation,
} from '@/rtkApi'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import { MRDiffDrawer } from './components/MRDiffDrawer'
import type { DiffNode } from './diffEngine'
import { toApiDecision, toApiFieldPath } from './mapMergeRequest'
import {
	aggregateMr,
	buildTree,
	countStructure,
	DECISION,
	getMergeRequestPath,
	inScope,
	MR_STATUS,
	positiveVotes,
	STRUCTURE_TYPE_META,
	unresolvedCount,
	userAppearance,
	type CreateMergeRequestNavigation,
} from './mrHelpers'
import { DecisionKind, MergeRequestRecord, MergeRequestStatus, MrStructure } from './types'
import { useMergeRequests } from './useMergeRequests'

const FILTERS: Array<MergeRequestStatus | 'ALL'> = [
	'ALL',
	'OPEN',
	'CHANGES_REQUESTED',
	'APPROVED',
	'MERGED',
	'REJECTED',
	'CLOSED',
	'STALE',
]

const Avatar = ({
	id,
	name,
	size = 26,
}: {
	id?: string | number | null
	name?: string
	size?: number
}) => {
	const u = userAppearance(name, id)
	return (
		<Box
			title={u.name}
			sx={{
				width: size,
				height: size,
				borderRadius: '50%',
				flex: '0 0 auto',
				bgcolor: u.color,
				color: '#fff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: size * 0.4,
				fontWeight: 700,
			}}
		>
			{u.initials}
		</Box>
	)
}

const StatusBadge = ({ status, big }: { big?: boolean, status: MergeRequestStatus; }) => {
	const { t } = useAppContext()
	const m = MR_STATUS[status] || MR_STATUS.OPEN
	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				flex: '0 0 auto',
				fontSize: big ? '11px' : '9.5px',
				fontWeight: 700,
				letterSpacing: '.03em',
				textTransform: 'uppercase',
				color: m.color,
				bgcolor: m.bg,
				borderRadius: '6px',
				px: big ? '9px' : '6px',
				py: big ? '3px' : '2px',
				whiteSpace: 'nowrap',
			}}
		>
			{t(m.labelKey)}
		</Box>
	)
}

const CountPips = ({
	counts,
}: {
	counts: { added: number; conflict: number, modified: number; removed: number; }
}) => {
	const { t } = useAppContext()
	const pip = (n: number, color: string, title: string) =>
		n ? (
			<Tooltip key={title} title={title} arrow>
				<Box
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: '3px',
						fontSize: '11px',
						fontWeight: 700,
						color,
					}}
				>
					<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
					{n}
				</Box>
			</Tooltip>
		) : null
	const items = [
		pip(counts.added, '#0F9D58', t('MERGE_REQUESTS_ADDED')),
		pip(counts.removed, '#C5221F', t('MERGE_REQUESTS_REMOVED')),
		pip(counts.modified, '#C26401', t('MERGE_REQUESTS_MODIFIED')),
		pip(counts.conflict, '#C5221F', t('MERGE_REQUESTS_CONFLICTS')),
	].filter(Boolean)
	return (
		<Box sx={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
			{items.length ? items : <Box sx={{ fontSize: '11px', color: '#B0B0B0' }}>{t('MERGE_REQUESTS_NO_CHANGES')}</Box>}
		</Box>
	)
}

const BranchArrow = ({ source, target }: { source: string; target: string }) => (
	<Box
		sx={{
			display: 'inline-flex',
			alignItems: 'center',
			gap: '6px',
			fontSize: '11.5px',
			color: '#52666C',
			minWidth: 0,
		}}
	>
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: 180, minWidth: 0 }}>
			<CallSplitOutlinedIcon sx={{ fontSize: 12, color: '#1F4954' }} />
			<span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
				{source}
			</span>
		</Box>
		<ArrowForwardOutlinedIcon sx={{ fontSize: 13, color: '#B0B0B0' }} />
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: 160, minWidth: 0 }}>
			<CallSplitOutlinedIcon sx={{ fontSize: 12, color: '#5B43C4' }} />
			<span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
				{target}
			</span>
		</Box>
	</Box>
)

const TypeIcon = ({ type }: { type: string }) => {
	if (type === 'diagram') return <SchemaOutlinedIcon sx={{ fontSize: 17 }} />
	if (type === 'mapping' || type === 'mapping_read' || type === 'mapping_write') {
		return <AccountTreeOutlinedIcon sx={{ fontSize: 17 }} />
	}
	if (type === 'expectation') return <FactCheckOutlinedIcon sx={{ fontSize: 17 }} />
	return <TableChartOutlinedIcon sx={{ fontSize: 17 }} />
}

const Timeline = ({
	events,
}: {
	events: MergeRequestRecord['timeline']
}) => {
	const { t } = useAppContext()
	const KIND: Record<string, { color: string, icon: React.ReactNode; }> = {
		created: { icon: <AddOutlinedIcon sx={{ fontSize: 13 }} />, color: '#1F4954' },
		approved: { icon: <CheckOutlinedIcon sx={{ fontSize: 13 }} />, color: '#0F9D58' },
		changes: { icon: <EditOutlinedIcon sx={{ fontSize: 13 }} />, color: '#C26401' },
		rejected: { icon: <CloseOutlinedIcon sx={{ fontSize: 13 }} />, color: '#C5221F' },
		closed: { icon: <CloseOutlinedIcon sx={{ fontSize: 13 }} />, color: '#7A7A7A' },
		merged: { icon: <MergeOutlinedIcon sx={{ fontSize: 13 }} />, color: '#5B43C4' },
		stale: { icon: <ErrorOutlineOutlinedIcon sx={{ fontSize: 13 }} />, color: '#B0863A' },
		conflict: { icon: <ErrorOutlineOutlinedIcon sx={{ fontSize: 13 }} />, color: '#C5221F' },
	}
	const labelOf = (ev: MergeRequestRecord['timeline'][number]) => {
		const actor = ev.actor
		if (ev.kind === 'created') return t('MERGE_REQUESTS_EVENT_CREATED')
		if (ev.kind === 'approved') return t('MERGE_REQUESTS_EVENT_APPROVED_BY', [actor])
		if (ev.kind === 'changes') return t('MERGE_REQUESTS_EVENT_REQUESTED_CHANGES', [actor])
		if (ev.kind === 'rejected') return t('MERGE_REQUESTS_EVENT_REJECTED_BY', [actor])
		if (ev.kind === 'closed') return t('MERGE_REQUESTS_EVENT_MR_CLOSED')
		if (ev.kind === 'merged') return ev.text || t('MERGE_REQUESTS_EVENT_MERGE_COMPLETED')
		return ev.text
	}
	return (
		<Box sx={{ pl: '4px' }}>
			{events.map((ev, i) => {
				const k = KIND[ev.kind] || KIND.created
				const last = i === events.length - 1
				const label = labelOf(ev)
				const extra =
					ev.kind === 'merged' ? '' : ev.text
				return (
					<Box key={i} sx={{ display: 'flex', gap: '12px', minHeight: last ? 'auto' : 34 }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								flex: '0 0 auto',
							}}
						>
							<Box
								sx={{
									width: 24,
									height: 24,
									borderRadius: '50%',
									bgcolor: `${k.color}1A`,
									color: k.color,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flex: '0 0 auto',
								}}
							>
								{k.icon}
							</Box>
							{!last ? (
								<Box sx={{ flex: 1, width: 2, bgcolor: '#E4E4E4', mt: '2px', minHeight: 12 }} />
							) : null}
						</Box>
						<Box sx={{ pb: last ? 0 : '14px', pt: '2px' }}>
							<Box sx={{ fontSize: '12.5px', color: '#2A2A2A', fontWeight: 600 }}>{label}</Box>
							{extra ? (
								<Box sx={{ fontSize: '11px', color: '#52666C', mt: '2px' }}>{extra}</Box>
							) : null}
							<Box sx={{ fontSize: '10.5px', color: '#9A9A9A', mt: '1px' }}>{ev.at}</Box>
						</Box>
					</Box>
				)
			})}
		</Box>
	)
}

const ApprovalPanel = ({
	currentUserId,
	mr,
	unresolved,
	onDecision,
	onMerge,
	onClose,
	onReopen,
}: {
	currentUserId?: number
	mr: MergeRequestRecord
	onClose: () => void
	onDecision: (d: DecisionKind) => void
	onMerge: () => void
	onReopen: () => void
	unresolved: number
}) => {
	const { t } = useAppContext()
	const pos = positiveVotes(mr)
	const reached = pos >= mr.requiredApprovals
	const isAuthor =
		currentUserId != null &&
		mr.authorId != null &&
		Number(mr.authorId) === Number(currentUserId)
	const cannotSelfApprove = isAuthor && !mr.selfApprovalAllowed
	const canVote = (['OPEN', 'CHANGES_REQUESTED', 'APPROVED', 'STALE'] as MergeRequestStatus[]).includes(
		mr.status,
	)
	const showMergeBtn = mr.status === 'APPROVED'

	return (
		<Box
			sx={{
				width: 320,
				flex: '0 0 auto',
				borderLeft: '1px solid #E4E4E4',
				bgcolor: '#fff',
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
			}}
		>
			<Box sx={{ px: '16px', pt: '15px', pb: '12px', borderBottom: '1px solid #ECECEC' }}>
				<Box
					sx={{
						fontSize: '11px',
						fontWeight: 700,
						letterSpacing: '.06em',
						textTransform: 'uppercase',
						color: '#6A6A6A',
						mb: '10px',
					}}
				>
					{t('MERGE_REQUESTS_APPROVALS')}
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Box sx={{ fontSize: '22px', fontWeight: 800, color: reached ? '#0F9D58' : '#1F4954' }}>
						{pos}/{mr.requiredApprovals}
					</Box>
					<Box sx={{ flex: 1 }}>
						<Box sx={{ fontSize: '11px', color: '#6A6A6A', fontWeight: 600 }}>
							{reached
								? t('MERGE_REQUESTS_QUORUM_REACHED')
								: t('MERGE_REQUESTS_APPROVALS_NEEDED')}
						</Box>
						<Box sx={{ mt: '5px', height: 6, borderRadius: 3, bgcolor: '#ECECEC', overflow: 'hidden' }}>
							<Box
								sx={{
									width: `${Math.min(100, (pos / mr.requiredApprovals) * 100)}%`,
									height: '100%',
									bgcolor: reached ? '#0F9D58' : '#1F4954',
									transition: 'width .2s',
								}}
							/>
						</Box>
					</Box>
				</Box>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '16px' }}>
				{mr.approvals.length ? (
					mr.approvals.map((a) => {
						const d = DECISION[a.decision] || DECISION.APPROVED
						return (
							<Box
								key={`${a.userId}-${a.at}`}
								sx={{ display: 'flex', gap: '10px', py: '10px', borderTop: '1px solid #EFEFEF' }}
							>
								<Avatar id={a.userId} name={a.name} size={30} />
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
										<Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#2A2A2A' }}>
											{a.name}
										</Box>
										<Box
											sx={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: '3px',
												fontSize: '9.5px',
												fontWeight: 700,
												textTransform: 'uppercase',
												color: d.color,
												bgcolor: d.bg,
												borderRadius: '5px',
												px: '5px',
												py: '1.5px',
											}}
										>
											{t(d.labelKey)}
										</Box>
									</Box>
									<Box sx={{ fontSize: '10.5px', color: '#9A9A9A', mt: '1px' }}>{a.at}</Box>
									{a.comment ? (
										<Box
											sx={{
												fontSize: '11.5px',
												color: '#52666C',
												mt: '4px',
												lineHeight: 1.45,
												bgcolor: '#F5F5F5',
												borderRadius: '7px',
												p: '7px 9px',
											}}
										>
											{a.comment}
										</Box>
									) : null}
								</Box>
							</Box>
						)
					})
				) : (
					<Box sx={{ py: '24px', textAlign: 'center', color: '#B0B0B0', fontSize: '12px' }}>
						{t('MERGE_REQUESTS_NO_DECISIONS')}
					</Box>
				)}
			</Box>

			<Box
				sx={{
					p: '14px 16px',
					borderTop: '1px solid #ECECEC',
					display: 'flex',
					flexDirection: 'column',
					gap: '8px',
				}}
			>
				{mr.status === 'MERGED' || mr.status === 'REJECTED' || mr.status === 'CLOSED' ? (
					<Box sx={{ textAlign: 'center', fontSize: '12px', color: '#9A9A9A', py: '4px' }}>
						{t('MERGE_REQUESTS_MR_CLOSED')}
					</Box>
				) : (
					<>
						{showMergeBtn ? (
							<Button
								variant="contained"
								disableElevation
								fullWidth
								startIcon={<MergeOutlinedIcon sx={{ fontSize: 16 }} />}
								onClick={onMerge}
								sx={{
									borderRadius: '9px',
									height: 40,
									bgcolor: '#5B43C4',
									'&:hover': { bgcolor: '#4E39AC' },
								}}
							>
								{t('MERGE_REQUESTS_MERGE')}
							</Button>
						) : null}
						{isAuthor && mr.status === 'CHANGES_REQUESTED' ? (
							<Button
								variant="contained"
								disableElevation
								fullWidth
								onClick={onReopen}
								sx={{ borderRadius: '9px', height: 40 }}
							>
								{t('MERGE_REQUESTS_MARK_OPEN')}
							</Button>
						) : null}
						<Tooltip
							title={
								unresolved
									? t('MERGE_REQUESTS_RESOLVE_CONFLICTS_FIRST', [String(unresolved)])
									: cannotSelfApprove
										? t('MERGE_REQUESTS_CANNOT_SELF_APPROVE')
										: ''
							}
							arrow
							disableHoverListener={!unresolved && !cannotSelfApprove}
						>
							<span style={{ width: '100%' }}>
								<Button
									variant="contained"
									color="success"
									disableElevation
									fullWidth
									disabled={unresolved > 0 || !canVote || cannotSelfApprove}
									startIcon={<CheckOutlinedIcon sx={{ fontSize: 16 }} />}
									onClick={() => onDecision('APPROVED')}
									sx={{ borderRadius: '9px', height: 40 }}
								>
									{t('MERGE_REQUESTS_APPROVE')}
								</Button>
							</span>
						</Tooltip>
						<Box sx={{ display: 'flex', gap: '8px' }}>
							<Button
								variant="outlined"
								fullWidth
								disabled={!canVote}
								onClick={() => onDecision('CHANGES_REQUESTED')}
								sx={{
									borderRadius: '9px',
									height: 38,
									textTransform: 'none',
									borderColor: '#E0A94E',
									color: '#C26401',
									'&:hover': { borderColor: '#C26401', bgcolor: '#FEF7EA' },
								}}
							>
								{t('MERGE_REQUESTS_REQUEST_CHANGES')}
							</Button>
							<Button
								variant="outlined"
								fullWidth
								disabled={!canVote}
								onClick={() => onDecision('REJECTED')}
								sx={{
									borderRadius: '9px',
									height: 38,
									textTransform: 'none',
									borderColor: '#E5B4B2',
									color: '#C5221F',
									'&:hover': { borderColor: '#C5221F', bgcolor: '#FDECEB' },
								}}
							>
								{t('MERGE_REQUESTS_REJECT')}
							</Button>
						</Box>
						{isAuthor ? (
							<Button
								variant="text"
								fullWidth
								onClick={onClose}
								sx={{ borderRadius: '9px', color: '#9A9A9A', textTransform: 'none' }}
							>
								{t('MERGE_REQUESTS_CLOSE_MR')}
							</Button>
						) : null}
					</>
				)}
			</Box>
		</Box>
	)
}

const branchName = (branches: BranchRecord[], id: string) =>
	branches.find((branch) => branch.id === id)?.name ?? id

const CreateMRDialog = ({
	branches,
	initialSourceId,
	initialTargetId,
	open,
	onClose,
	onCreate,
}: {
	branches: BranchRecord[]
	initialSourceId?: string | null
	initialTargetId?: string | null
	onClose: () => void,
	onCreate: (data: {
		description: string,
		sourceBranchId: string,
		targetBranchId: string,
		title: string
	}) => void,
	open: boolean
}) => {
	const { t } = useAppContext()
	const sysBranches = branches.filter((branch) => branch.status !== 'DELETED')
	const [title, setTitle] = useState('')
	const [desc, setDesc] = useState('')
	const [srcB, setSrcB] = useState('')
	const [tgtB, setTgtB] = useState('')

	useEffect(() => {
		if (!open || !initialSourceId) return
		const selectable = new Set(sysBranches.map((branch) => branch.id))
		if (!selectable.has(initialSourceId)) return
		setSrcB((current) => current || initialSourceId)
		if (
			initialTargetId &&
			initialTargetId !== initialSourceId &&
			selectable.has(initialTargetId)
		) {
			setTgtB((current) => current || initialTargetId)
		}
	}, [initialSourceId, initialTargetId, open, sysBranches])

	const reset = () => {
		setTitle('')
		setDesc('')
		setSrcB('')
		setTgtB('')
	}
	const close = () => {
		reset()
		onClose()
	}
	const canCreate = Boolean(title.trim() && srcB && tgtB && srcB !== tgtB)

	const submit = () => {
		onCreate({
			title: title.trim(),
			description: desc.trim(),
			sourceBranchId: srcB,
			targetBranchId: tgtB,
		})
		close()
	}

	if (!open) return null

	return (
		<Box
			sx={{
				position: 'fixed',
				inset: 0,
				zIndex: 1300,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				p: '20px',
			}}
		>
			<Box onClick={close} sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.42)' }} />
			<Box
				sx={{
					position: 'relative',
					width: 'min(600px, 96vw)',
					maxHeight: '90vh',
					bgcolor: '#fff',
					borderRadius: '14px',
					boxShadow: '0 16px 50px rgba(0,0,0,0.3)',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						fontSize: 16,
						fontWeight: 700,
						color: '#1F4954',
						px: '24px',
						pt: '20px',
						pb: '6px',
						flex: '0 0 auto',
					}}
				>
					{t('MERGE_REQUESTS_CREATE')}
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
						px: '24px',
						py: '8px',
						flex: 1,
						minHeight: 0,
						overflowY: 'auto',
					}}
				>
					<TextField
						size="small"
						fullWidth
						label={t('MERGE_REQUESTS_TITLE')}
						value={title}
						onChange={(ev) => setTitle(ev.target.value)}
						slotProps={{ input: { sx: { borderRadius: '9px', fontSize: '13px' } } }}
					/>
					<Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
						<FormControl size="small" fullWidth>
							<InputLabel>{t('MERGE_REQUESTS_SOURCE_BRANCH')}</InputLabel>
							<Select
								label={t('MERGE_REQUESTS_SOURCE_BRANCH')}
								value={srcB}
								onChange={(ev) => setSrcB(ev.target.value)}
								sx={{ borderRadius: '9px', fontSize: '13px' }}
							>
								{sysBranches.map((b) => (
									<MenuItem key={b.id} value={b.id} sx={{ fontSize: 13, gap: '8px' }}>
										<CallSplitOutlinedIcon sx={{ fontSize: 14, color: '#52666C' }} />
										{b.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<ArrowForwardOutlinedIcon sx={{ fontSize: 16, color: '#B0B0B0', flex: '0 0 auto' }} />
						<FormControl size="small" fullWidth>
							<InputLabel>{t('MERGE_REQUESTS_TARGET_BRANCH')}</InputLabel>
							<Select
								label={t('MERGE_REQUESTS_TARGET_BRANCH')}
								value={tgtB}
								onChange={(ev) => setTgtB(ev.target.value)}
								sx={{ borderRadius: '9px', fontSize: '13px' }}
							>
								{sysBranches.map((b) => (
									<MenuItem key={b.id} value={b.id} sx={{ fontSize: 13, gap: '8px' }}>
										<CallSplitOutlinedIcon sx={{ fontSize: 14, color: '#52666C' }} />
										{b.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					{srcB && tgtB && srcB === tgtB ? (
						<Box sx={{ fontSize: 11, color: '#C5221F' }}>
							{t('MERGE_REQUESTS_BRANCHES_MUST_DIFFER')}
						</Box>
					) : null}
					<TextField
						size="small"
						fullWidth
						label={t('MERGE_REQUESTS_DESCRIPTION')}
						value={desc}
						multiline
						minRows={2}
						onChange={(ev) => setDesc(ev.target.value)}
						slotProps={{ input: { sx: { borderRadius: '9px', fontSize: '13px' } } }}
					/>
				</Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '8px',
						px: '24px',
						py: '16px',
						borderTop: '1px solid #EEE',
						flex: '0 0 auto',
					}}
				>
					<Button onClick={close} sx={{ color: '#6A6A6A', borderRadius: '8px' }}>
						{t('CANCEL')}
					</Button>
					<Button
						variant="contained"
						disableElevation
						disabled={!canCreate}
						onClick={submit}
						sx={{ borderRadius: '8px', px: '18px' }}
					>
						{t('MERGE_REQUESTS_CREATE_MR')}
					</Button>
				</Box>
			</Box>
		</Box>
	)
}

export const MergeRequestsLayout = () => {
	const { t } = useAppContext()
	const navigate = useNavigate()
	const location = useLocation()
	const { mrId } = useParams<{ mrId?: string }>()
	const currentUserId = useAppStore((state) => state.auth.user?.id)
	const [filter, setFilter] = useState<MergeRequestStatus | 'ALL'>('ALL')
	const [page, setPage] = useState(1)
	const {
		branches,
		hasSystemId,
		isError,
		isLoading,
		mergeRequests,
		selected,
		totalElements,
		totalPages,
	} = useMergeRequests(mrId, {
		page,
		status: filter === 'ALL' ? undefined : filter,
	})
	const [createOpen, setCreateOpen] = useState(false)
	const [createSourceId, setCreateSourceId] = useState<string | null>(null)
	const [createTargetId, setCreateTargetId] = useState<string | null>(null)
	const [diffStruct, setDiffStruct] = useState<MrStructure | null>(null)
	const [decisionPrompt, setDecisionPrompt] = useState<
		'CHANGES_REQUESTED' | 'REJECTED' | null
	>(null)
	const [createMergeRequest] = useCreateMergeRequestMutation()
	const [decide] = useDecideMutation()
	const [executeMerge] = useExecuteMergeMutation()
	const [saveResolution] = useSaveResolutionMutation()
	const [updateStatus] = useUpdateMergeRequestStatusMutation()

	const shown = mergeRequests
	const openMr = (id: string) => navigate(getMergeRequestPath(id))
	const backToList = () => navigate(getMergeRequestPath())
	const createRequest = location.state as CreateMergeRequestNavigation | null

	useEffect(() => {
		if (!createRequest?.createMergeSourceBranchId) return
		setCreateSourceId(createRequest.createMergeSourceBranchId)
		setCreateTargetId(createRequest.createMergeTargetBranchId)
		setCreateOpen(true)
		navigate(getMergeRequestPath(), { replace: true, state: null })
	}, [createRequest, navigate])

	const requireUserId = () => {
		if (currentUserId == null) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.requireUser' },
				error: new Error('Missing user id'),
				messageKey: 'MERGE_REQUESTS_NO_USER',
				t,
			})
			return null
		}
		return currentUserId
	}

	const applyDecision = async (decision: DecisionKind, comment = '') => {
		const mr = selected
		const userId = requireUserId()
		if (!mr || userId == null) return
		try {
			await decide({
				mergeRequestId: Number(mr.id),
				userId,
				decision: toApiDecision(decision),
				comment: comment || undefined,
			}).unwrap()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.applyDecision' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'MERGE_REQUESTS_DECISION_FAILED',
				t,
			})
		}
	}

	const requestDecision = (decision: DecisionKind) => {
		if (decision === 'CHANGES_REQUESTED' || decision === 'REJECTED') {
			setDecisionPrompt(decision)
			return
		}
		void applyDecision(decision)
	}

	const doMerge = async () => {
		const mr = selected
		const userId = requireUserId()
		if (!mr || userId == null) return
		try {
			await executeMerge({ mergeRequestId: Number(mr.id), userId }).unwrap()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.merge' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'MERGE_REQUESTS_MERGE_FAILED',
				t,
			})
		}
	}

	const resolveConflict = async (
		structure: MrStructure,
		node: DiffNode,
		resolution: 'source' | 'target' | 'custom',
		customValue?: unknown,
	) => {
		const mr = selected
		if (!mr) return
		const selectedValue =
			resolution === 'source'
				? node.source
				: resolution === 'target'
					? node.target
					: customValue
		const serialize = (value: unknown) => {
			const json = JSON.stringify(value)
			return json === undefined ? String(value ?? null) : json
		}
		try {
			await saveResolution({
				mergeRequestId: Number(mr.id),
				structureId: structure.key,
				fieldPath: toApiFieldPath(node.path),
				sourceValue: serialize(node.source),
				targetValue: serialize(node.target),
				selectedValue: serialize(selectedValue),
				resolution,
			}).unwrap()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.resolveConflict' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'MERGE_REQUESTS_RESOLUTION_FAILED',
				t,
			})
			throw error
		}
	}

	const doReopen = async () => {
		const mr = selected
		if (!mr) return
		try {
			await updateStatus({
				mergeRequestId: Number(mr.id),
				status: 'OPEN',
			}).unwrap()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.reopen' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'MERGE_REQUESTS_REOPEN_FAILED',
				t,
			})
		}
	}

	const doClose = async () => {
		const mr = selected
		if (!mr) return
		try {
			await updateStatus({
				mergeRequestId: Number(mr.id),
				status: 'CLOSED',
			}).unwrap()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.close' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'MERGE_REQUESTS_CLOSE_FAILED',
				t,
			})
		}
	}

	const createMr = async (data: {
		description: string,
		sourceBranchId: string,
		targetBranchId: string,
		title: string
	}) => {
		try {
			const created = await createMergeRequest({
				sourceBranchId: Number(data.sourceBranchId),
				targetBranchId: Number(data.targetBranchId),
				mergeRequestDto: {
					title: data.title,
					description: data.description || undefined,
					scope: 'FULL',
				},
			}).unwrap()
			if (created.id != null) navigate(getMergeRequestPath(String(created.id)))
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'MergeRequests.create' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'MERGE_REQUESTS_CREATE_FAILED',
				t,
			})
		}
	}

	const listView = (
		<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: '#F7F7F7', position: 'relative' }}>
			<Loader loaded={!isLoading} $absolute />
			<Box sx={{ px: '26px', pt: '20px', pb: '14px', bgcolor: '#fff', borderBottom: '1px solid #E4E4E4' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
					<Box sx={{ color: '#1F4954', display: 'flex' }}>
						<MergeOutlinedIcon sx={{ fontSize: 20 }} />
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#1F4954', letterSpacing: '.01em' }}>
							{t('MERGE_REQUESTS_PAGE_TITLE')}
						</Typography>
						<Typography sx={{ fontSize: '12px', color: '#9A9A9A' }}>
							{t('MERGE_REQUESTS_PAGE_SUBTITLE')}
						</Typography>
					</Box>
					<Button
						variant="contained"
						disableElevation
						disabled={!hasSystemId}
						startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
						onClick={() => {
							setCreateSourceId(null)
							setCreateTargetId(null)
							setCreateOpen(true)
						}}
						sx={{ borderRadius: '9px', px: '16px', height: 38 }}
					>
						{t('MERGE_REQUESTS_CREATE_MR')}
					</Button>
				</Box>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '14px' }}>
					{FILTERS.map((f) => {
						const on = filter === f
						const label = f === 'ALL' ? t('MERGE_REQUESTS_FILTER_ALL') : t(MR_STATUS[f].labelKey)
						return (
							<Box
								key={f}
								onClick={() => {
									setFilter(f)
									setPage(1)
								}}
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: '6px',
									px: '11px',
									height: 28,
									cursor: 'pointer',
									borderRadius: '8px',
									fontSize: '11.5px',
									fontWeight: 600,
									border: '1px solid',
									whiteSpace: 'nowrap',
									...(on
										? { bgcolor: '#1F4954', color: '#fff', borderColor: '#1F4954' }
										: {
												bgcolor: '#fff',
												color: '#52666C',
												borderColor: '#DCDCDC',
												'&:hover': { borderColor: '#1F4954' },
											}),
								}}
							>
								{label}
								{on && (
									<Box sx={{ fontSize: '10px', fontWeight: 700, opacity: 0.85 }}>
										{totalElements}
									</Box>
								)}
							</Box>
						)
					})}
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 26px' }}>
				{!hasSystemId ? (
					<Box sx={{ p: '40px', textAlign: 'center', color: '#B0B0B0', fontSize: '13px' }}>
						{t('MERGE_REQUESTS_NO_SYSTEM_HINT')}
					</Box>
				) : isError ? (
					<Box sx={{ p: '40px', textAlign: 'center', color: '#B0B0B0', fontSize: '13px' }}>
						{t('MERGE_REQUESTS_LOAD_FAILED')}
					</Box>
				) : (
					<>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '2.4fr 2fr 1fr 1.1fr 1fr 0.9fr',
								gap: '12px',
								px: '16px',
								pb: '8px',
								fontSize: '10px',
								fontWeight: 700,
								letterSpacing: '.05em',
								textTransform: 'uppercase',
								color: '#9A9A9A',
							}}
						>
							{[
								t('MERGE_REQUESTS_COL_MR'),
								t('MERGE_REQUESTS_COL_BRANCHES'),
								t('MERGE_REQUESTS_AUTHOR'),
								t('MERGE_REQUESTS_CHANGES'),
								t('MERGE_REQUESTS_QUORUM'),
								t('MERGE_REQUESTS_STATUS'),
							].map((h) => (
								<Box key={h}>{h}</Box>
							))}
						</Box>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							{shown.length ? (
								shown.map((mr) => {
									const agg = aggregateMr(mr)
									const pos = positiveVotes(mr)
									return (
										<Box
											key={mr.id}
											onClick={() => openMr(mr.id)}
											sx={{
												display: 'grid',
												gridTemplateColumns: '2.4fr 2fr 1fr 1.1fr 1fr 0.9fr',
												gap: '12px',
												alignItems: 'center',
												bgcolor: '#fff',
												border: '1px solid #E7E7E7',
												borderRadius: '10px',
												px: '16px',
												py: '12px',
												cursor: 'pointer',
												transition: 'border-color .12s, box-shadow .12s',
												'&:hover': {
													borderColor: '#1F4954',
													boxShadow: '0 2px 10px rgba(31,73,84,0.08)',
												},
											}}
										>
											<Box sx={{ minWidth: 0 }}>
												<Box
													sx={{
														fontSize: '13.5px',
														fontWeight: 700,
														color: '#1F4954',
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{mr.title}
												</Box>
												<Box sx={{ fontSize: '10.5px', color: '#9A9A9A', mt: '2px' }}>
													{mr.createdAt}
													{mr.scope === 'PARTIAL' ? t('MERGE_REQUESTS_CHERRY_PICK') : ''}
												</Box>
											</Box>
											<BranchArrow
												source={branchName(branches, mr.sourceBranchId)}
												target={branchName(branches, mr.targetBranchId)}
											/>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
												<Avatar
													id={mr.authorId}
													name={mr.author}
													size={24}
												/>
												<Box
													sx={{
														fontSize: '11.5px',
														color: '#52666C',
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{mr.author}
												</Box>
											</Box>
											<CountPips counts={agg.counts} />
											<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
												<Box
													sx={{
														fontSize: '12.5px',
														fontWeight: 700,
														color: pos >= mr.requiredApprovals ? '#0F9D58' : '#52666C',
													}}
												>
													{pos}/{mr.requiredApprovals}
												</Box>
											</Box>
											<StatusBadge status={mr.status} />
										</Box>
									)
								})
							) : (
								<Box sx={{ p: '40px', textAlign: 'center', color: '#B0B0B0', fontSize: '13px' }}>
									{t('MERGE_REQUESTS_EMPTY_FILTER')}
								</Box>
							)}
						</Box>
						{totalPages > 1 && (
							<Box sx={{ display: 'flex', justifyContent: 'center', pt: '18px' }}>
								<Pagination
									count={totalPages}
									page={page}
									onChange={(_event, nextPage) => setPage(nextPage)}
									size="small"
									shape="rounded"
								/>
							</Box>
						)}
					</>
				)}
			</Box>
		</Box>
	)

	const detailView = selected ? (
		(() => {
			const mr = selected
			const agg = aggregateMr(mr)
			const scoped = inScope(mr)
			const banner = (() => {
				if (mr.status === 'STALE')
					return {
						color: '#B0863A',
						bg: '#FBF3E2',
						icon: <ErrorOutlineOutlinedIcon sx={{ fontSize: 16 }} />,
						text: t('MERGE_REQUESTS_EVENT_TARGET_CHANGED'),
					}
				if (mr.status === 'MERGED')
					return {
						color: '#5B43C4',
						bg: '#EEEAFA',
						icon: <MergeOutlinedIcon sx={{ fontSize: 16 }} />,
						text: t('MERGE_REQUESTS_EVENT_MERGED_INTO_TARGET'),
					}
				if (mr.status === 'APPROVED')
					return {
						color: '#0F9D58',
						bg: '#E6F4EA',
						icon: <CheckOutlinedIcon sx={{ fontSize: 16 }} />,
						text: t('MERGE_REQUESTS_EVENT_QUORUM_COMPLETE_MERGE'),
					}
				return null
			})()

			return (
				<Box sx={{ flex: 1, minWidth: 0, display: 'flex', minHeight: 0 }}>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: 'flex',
							flexDirection: 'column',
							bgcolor: '#F7F7F7',
						}}
					>
						<Box
							sx={{
								px: '26px',
								pt: '16px',
								pb: '16px',
								bgcolor: '#fff',
								borderBottom: '1px solid #E4E4E4',
							}}
						>
							<Box
								onClick={backToList}
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: '5px',
									cursor: 'pointer',
									color: '#6A6A6A',
									fontSize: '11.5px',
									fontWeight: 600,
									mb: '10px',
									'&:hover': { color: '#1F4954' },
								}}
							>
								<ArrowBackOutlinedIcon sx={{ fontSize: 13 }} />
								{t('MERGE_REQUESTS_BACK_TO_LIST')}
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
										<Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#1F4954' }}>
											{mr.title}
										</Typography>
										<StatusBadge status={mr.status} big />
									</Box>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '14px',
											mt: '8px',
											flexWrap: 'wrap',
										}}
									>
										<BranchArrow
											source={branchName(branches, mr.sourceBranchId)}
											target={branchName(branches, mr.targetBranchId)}
										/>
										<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
											<Avatar
												id={mr.authorId}
												name={mr.author}
												size={22}
											/>
											<Box sx={{ fontSize: '11.5px', color: '#52666C' }}>
												{mr.author}
											</Box>
										</Box>
										<Box sx={{ fontSize: '11.5px', color: '#9A9A9A' }}>{mr.createdAt}</Box>
									</Box>
								</Box>
							</Box>
							{mr.description ? (
								<Box
									sx={{
										fontSize: '12.5px',
										color: '#52666C',
										lineHeight: 1.55,
										mt: '12px',
										maxWidth: 760,
									}}
								>
									{mr.description}
								</Box>
							) : null}
						</Box>

						<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '26px', py: '18px' }}>
							{banner ? (
								<Box
									sx={{
										display: 'flex',
										gap: '9px',
										alignItems: 'center',
										p: '11px 14px',
										borderRadius: '10px',
										bgcolor: banner.bg,
										color: banner.color,
										mb: '16px',
										fontSize: '12.5px',
										fontWeight: 600,
									}}
								>
									{banner.icon}
									{banner.text}
								</Box>
							) : null}

							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '9px' }}>
								<Box
									sx={{
										fontSize: '11px',
										fontWeight: 700,
										letterSpacing: '.05em',
										textTransform: 'uppercase',
										color: '#6A6A6A',
									}}
								>
									{t('MERGE_REQUESTS_AFFECTED_STRUCTURES')}
								</Box>
								<Box sx={{ fontSize: '11px', color: '#9A9A9A' }}>
									{t('MERGE_REQUESTS_STRUCTURES_SUMMARY', [
										String(scoped.length),
										String(agg.counts.conflict),
									])}
								</Box>
							</Box>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
								{scoped.map((s) => {
									const tree = buildTree(s)
									const c = countStructure(tree)
									const un = unresolvedCount(tree)
									const meta = STRUCTURE_TYPE_META[s.type] || STRUCTURE_TYPE_META.table
									return (
										<Box
											key={s.key}
											onClick={() => setDiffStruct(s)}
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: '12px',
												bgcolor: '#fff',
												border: '1px solid #E7E7E7',
												borderRadius: '10px',
												px: '14px',
												py: '11px',
												cursor: 'pointer',
												'&:hover': {
													borderColor: '#1F4954',
													boxShadow: '0 2px 8px rgba(31,73,84,0.07)',
												},
											}}
										>
											<Box sx={{ color: meta.color, display: 'flex', flex: '0 0 auto' }}>
												<TypeIcon type={s.type} />
											</Box>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box
													sx={{
														fontSize: '13px',
														fontWeight: 600,
														color: '#2A2A2A',
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{s.label}
												</Box>
												<Box
													sx={{
														fontSize: '10px',
														color: '#B0B0B0',
														textTransform: 'uppercase',
														letterSpacing: '.03em',
													}}
												>
													{t(meta.labelKey)}
												</Box>
											</Box>
											<CountPips counts={c} />
											{un ? (
												<Box
													sx={{
														display: 'inline-flex',
														alignItems: 'center',
														gap: '4px',
														fontSize: '10.5px',
														fontWeight: 700,
														color: '#C5221F',
														bgcolor: '#FDE7E6',
														borderRadius: '6px',
														px: '7px',
														py: '3px',
													}}
												>
													<ErrorOutlineOutlinedIcon sx={{ fontSize: 12 }} />
													{t('MERGE_REQUESTS_UNRESOLVED_COUNT', [String(un)])}
												</Box>
											) : c.conflict ? (
												<Box
													sx={{
														display: 'inline-flex',
														alignItems: 'center',
														gap: '4px',
														fontSize: '10.5px',
														fontWeight: 700,
														color: '#0F9D58',
														bgcolor: '#E6F4EA',
														borderRadius: '6px',
														px: '7px',
														py: '3px',
													}}
												>
													<CheckOutlinedIcon sx={{ fontSize: 12 }} />
													{t('MERGE_REQUESTS_RESOLVED')}
												</Box>
											) : null}
											<Box
												sx={{
													display: 'inline-flex',
													alignItems: 'center',
													gap: '5px',
													fontSize: '11.5px',
													fontWeight: 600,
													color: '#1F4954',
													flex: '0 0 auto',
												}}
											>
												<MergeOutlinedIcon sx={{ fontSize: 14 }} />
												{t('MERGE_REQUESTS_DIFF')}
											</Box>
										</Box>
									)
								})}
							</Box>

							<Box
								sx={{
									fontSize: '11px',
									fontWeight: 700,
									letterSpacing: '.05em',
									textTransform: 'uppercase',
									color: '#6A6A6A',
									mt: '26px',
									mb: '12px',
								}}
							>
								{t('MERGE_REQUESTS_HISTORY')}
							</Box>
							<Timeline events={mr.timeline} />
						</Box>
					</Box>
					<ApprovalPanel
						currentUserId={currentUserId}
						mr={mr}
						unresolved={agg.unresolved}
						onDecision={requestDecision}
						onMerge={() => void doMerge()}
						onClose={() => void doClose()}
						onReopen={() => void doReopen()}
					/>
				</Box>
			)
		})()
	) : null

	return (
		<Box sx={{ flex: 1, minWidth: 0, display: 'flex', minHeight: 0, height: '100%' }}>
			<Box sx={{ flex: 1, minWidth: 0, display: 'flex', minHeight: 0 }}>
				{selected ? detailView : listView}
			</Box>
			<MRDiffDrawer
				open={Boolean(diffStruct)}
				structure={diffStruct}
				sourceBranchName={
					selected ? branchName(branches, selected.sourceBranchId) : undefined
				}
				targetBranchName={
					selected ? branchName(branches, selected.targetBranchId) : undefined
				}
				readOnly={Boolean(
					selected &&
						(['MERGED', 'REJECTED', 'CLOSED'] as MergeRequestStatus[]).includes(
							selected.status,
						),
				)}
				onClose={() => setDiffStruct(null)}
				onResolve={resolveConflict}
			/>
			<CreateMRDialog
				branches={branches}
				initialSourceId={createSourceId}
				initialTargetId={createTargetId}
				open={createOpen}
				onClose={() => {
					setCreateOpen(false)
					setCreateSourceId(null)
					setCreateTargetId(null)
				}}
				onCreate={(data) => void createMr(data)}
			/>
			<PromptDialog
				open={decisionPrompt != null}
				title={
					decisionPrompt === 'REJECTED'
						? t('MERGE_REQUESTS_REJECTION_REASON')
						: t('MERGE_REQUESTS_CHANGES_PROMPT')
				}
				label={t('MERGE_REQUESTS_COMMENT_OPTIONAL')}
				multiline
				defaultValue=""
				onCancel={() => setDecisionPrompt(null)}
				onConfirm={(comment) => {
					if (decisionPrompt) void applyDecision(decisionPrompt, comment)
					setDecisionPrompt(null)
				}}
			/>
		</Box>
	)
}

